import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { LogOut, AlertTriangle, Shield, Crown, Eye } from "lucide-react";
import { api } from "~/services/api";
import { useUser, useSession } from "../lib/auth/useSession";

interface ImpersonationBannerProps {
  onStopImpersonation: () => void;
}

export default function ImpersonationBanner({ onStopImpersonation }: ImpersonationBannerProps) {
  const user = useUser();
  const { isLoading: userLoading } = useSession();
  const [status, setStatus] = useState<{
    is_impersonating: boolean;
    impersonating_email?: string;
    original_admin_email?: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Only load status after user data is available
    if (!userLoading && user) {
      loadStatus();
    } else if (!userLoading && !user) {
      setLoading(false);
    }
  }, [userLoading, user]);

  const loadStatus = async () => {
    try {
      const result = await api.getImpersonationStatus();
      setStatus(result);
    } catch (error) {
      console.error('Failed to load impersonation status:', error);
      setStatus({ is_impersonating: false });
    } finally {
      setLoading(false);
    }
  };

  const handleStop = async () => {
    try {
      await api.stopImpersonation();
      onStopImpersonation();
      // Reload the page to refresh the session
      window.location.reload();
    } catch (error) {
      console.error('Failed to stop impersonation:', error);
    }
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-r from-slate-50 to-blue-50 border-b border-slate-200">
        <div className="max-w-full mx-auto px-6 py-4">
          <div className="flex items-center justify-center gap-3">
            <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-slate-700 font-medium">Checking impersonation session...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!status?.is_impersonating) {
    return null;
  }

  return (
    <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 border-b border-gray-200 shadow-sm">
      <div className="max-w-full mx-auto px-6 py-4">
        <div className="flex items-center justify-between w-full">
          {/* Left side - Status info */}
          <div className="flex items-center gap-4">
            {/* Icon cluster */}
            <div className="flex items-center justify-center w-12 h-12 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
              <div className="relative">
                <Crown className="h-6 w-6 text-amber-300" />
                <Eye className="h-3 w-3 text-white absolute -top-1 -right-1" />
              </div>
            </div>
            
            {/* Status text */}
            <div className="flex flex-col">
              <div className="flex items-center gap-3">
                <span className="text-white font-semibold text-lg">
                  Impersonating {status.impersonating_email}
                </span>
                <Badge className="bg-amber-500 text-amber-950 hover:bg-amber-400 font-medium px-3 py-1">
                  <Shield className="h-3 w-3 mr-1" />
                  Admin Session
                </Badge>
              </div>
              <div className="text-indigo-100 text-sm mt-1">
                Original admin: <span className="font-medium text-white">{status.original_admin_email}</span>
              </div>
            </div>
          </div>

          {/* Right side - Actions */}
          <div className="flex items-center gap-3">
            {/* Warning indicator */}
            <div className="hidden sm:flex items-center gap-2 text-indigo-100 text-sm bg-white/5 px-3 py-2 rounded-lg border border-white/10">
              <AlertTriangle className="h-4 w-4 text-amber-300" />
              <span>All actions performed as impersonated user</span>
            </div>
            
            {/* Stop button */}
            <Button
              onClick={handleStop}
              size="lg"
              className="bg-white text-indigo-700 hover:bg-indigo-50 font-semibold shadow-lg border-0 px-6 py-3 transition-all duration-200 hover:shadow-xl"
            >
              <LogOut className="h-5 w-5 mr-2" />
              Stop Impersonation
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}